import { QueryBuilder } from 'src/app/angularJS/services/query-builder.service';
import { JourneyService } from 'src/app/angularJS/services/journey/journey.service';
import { UsageEventService } from 'src/app/angularJS/services/usage-event/usage-event.service';
import { UsageEventProductMappingsService } from 'src/app/angularJS/services/usageEventProductMappings/usageEventProductMappings.service';

export class ChargeAccountProductJourneysTableController {
  public static $inject = [
    'QueryBuilder',
    'journeyService',
    'usageEventService',
    'usageEventProductMappingsService',
    '$q',
    '$rootScope',
    '$scope'
  ];
  public paginatorData: InterACT.Interfaces.IPaginationData = {
    currentPage: 0,
    perPageCount: 15,
    totalCount: 0
  };

  public usageTokenLink: string;
  public usageEventProductMappingId: string;
  public journeys: Array<InterACT.Interfaces.Journeys.Journey> = [];
  public expandedItem: InterACT.Interfaces.Journeys.Journey;
  public journeyToUsageEventDictionary: InterACT.Interfaces.IDictionary<Array<InterACT.Interfaces.UsageEvents.UsageEvent>> = {};

  constructor(
    private queryBuilder: QueryBuilder,
    private journeyService: JourneyService,
    private usageEventService: UsageEventService,
    private usageEventProductMappingsService: UsageEventProductMappingsService,
    private $q: ng.IQService,
    private $rootScope: InterACT.Interfaces.IActoraRootScope,
    private $scope
  ) {
    // tslint:disable-next-line: forin
    for (const propertyName in this.$scope.vm) {
      this[propertyName] = this.$scope.vm[propertyName];
    }

    this.activate();
  }

  /**
   * A method that gets the mode of a given journey
   * @param journey the journey to get the mode for
   */
  public getJourneyMode(journey: InterACT.Interfaces.Journeys.Journey) {
    if (!journey.journeyLegs || journey.journeyLegs.length === 0) {
      return 'NA';
    }

    const firstJourneyLegMode = journey.journeyLegs[0].mode;
    const otherModes = journey.journeyLegs.filter((journeyLeg) => {
      return journeyLeg.mode !== firstJourneyLegMode;
    });

    return otherModes.length > 0 ? 'MULTIPLE' : firstJourneyLegMode;
  }

  /**
   * A method that toggles the expansion state for the supplied journey
   * @param journey the journey to toggle the expansion for
   */
  public toggleExpansion = (journey: InterACT.Interfaces.Journeys.Journey) => {
    if (this.expandedItem === journey) {
      this.expandedItem = undefined;
    } else {
      this.expandedItem = journey;
      this.populateJourneyToUsageEventDictionary(journey);
    }
  }

  /**
   * A method that retrieves a given page of journeys that related to the usage event product mapping id on this controller
   * @param page the page of journeys to retrieve
   * @param perPage the amount of journeys to retrieve per page
   */
  public getData(page: number, perPage: number) {
    if (!perPage) {
      perPage = this.paginatorData.perPageCount;
    }

    this.constructQueryToUseToRetrieveJourneys().then((query) => {
      this.$rootScope.showSpinner = true;
      this.journeyService.getList(page, perPage, query).then((getJourneysResponse: InterACT.Interfaces.Journeys.JourneysResponse) => {
        this.paginatorData.totalCount = getJourneysResponse.meta.totalCount;
        this.journeys = getJourneysResponse.journeys;
        this.$rootScope.showSpinner = false;
      });
    });
  }

  /**
   * A method that returns a description of the supplied tap event in the form LOCATION (Type). i.e. Cardiff Central (Entry)
   * @param usageEvent the usageEvent to get the description for
   */
  public getUsageDescription(usageEvent: InterACT.Interfaces.UsageEvents.UsageEvent) {
    const suffix = ' (' + usageEvent.type + ')';

    if (usageEvent.location && usageEvent.destinationLocation) {
      return usageEvent.location.locationName + ' > ' + usageEvent.destinationLocation.locationName;
    }

    if (usageEvent.location) {
      return usageEvent.location.locationName + suffix;
    }

    return usageEvent.destinationLocation.locationName + suffix;
  }

  /**
   * A method that returns a string of the direction of travel (DOT) if supplied or any otherwise
   * @param direction the DOT string to check
   */
  public getDirectionOfTravel(direction: string) {
    if (direction) {
      return 'ROUTE_DIRECTION.' + direction;
    }
    return 'ROUTE_DIRECTION.ALL';
  }

  /**
   * A method that constructs the query to use to return the journeys to be used on this controller
   */
  private constructQueryToUseToRetrieveJourneys() {
    const deferred = this.$q.defer<string>();
    let query;

    if (this.usageEventProductMappingId) {
      this.getUsageEventProductMapping(this.usageEventProductMappingId).then((usageEventProductMapping: InterACT.Interfaces.UsageEventProductMappings.UsageEventProductMapping) => {
        const journeyIds = usageEventProductMapping.relatedItems.map((journeyLink) => {
          return journeyLink.link.split('/').pop();
        });

        query = this.queryBuilder.newParam().fieldIn('id', journeyIds)
          .sortBy([{
            columnName: 'startDateTime',
            direction: 'desc'
          }]).getParam();

        deferred.resolve(query);
      });
    } else {
      query = this.queryBuilder.newParam().fieldIs('usageToken.link', this.usageTokenLink)
        .sortBy([{
          columnName: 'startDateTime',
          direction: 'desc'
        }]).getParam();

      deferred.resolve(query);
    }

    return deferred.promise;
  }

  /**
   * A method that retrieves the usageEventProductMapping with the supplied Id
   * @param usageEventProductMappingId the id of the uepm to retrieve
   */
  private getUsageEventProductMapping(usageEventProductMappingId: string) {
    const deferred = this.$q.defer();
    const query = this.queryBuilder.newParam().fieldIs('id', usageEventProductMappingId).getParam();

    this.usageEventProductMappingsService.getList(1, 1, query).then((response) => {
      const resolveWith = response.usageEventProductMappings.length > 0 ? response.usageEventProductMappings[0].usageEventProductMapping : undefined;
      deferred.resolve(resolveWith);
    });

    return deferred.promise;
  }

  /**
   * A method that retrieves, and stores in a dictionary, the usage events related to a journey
   * @param journey the journey to get and store the usage events for
   */
  private populateJourneyToUsageEventDictionary(journey: InterACT.Interfaces.Journeys.Journey) {
    if (!journey.usageEvents || journey.usageEvents.length === 0) {
      return;
    }

    if (this.journeyToUsageEventDictionary[journey.id]) {
      return;
    }

    const usageEventIds = journey.usageEvents.map((usageEvent) => {
      return usageEvent.link.split('/').pop();
    });

    const query = this.queryBuilder.newParam().fieldIn('id', usageEventIds).sortBy([{ columnName: 'timestamp', direction: 'desc' }]).getParam();
    this.usageEventService.getList(1, usageEventIds.length, query).then((response) => {
      this.journeyToUsageEventDictionary[journey.id] = response.usageEvents.map((usageEventWithMeta) => { return usageEventWithMeta.usageEvent; });
    });
  }

  /**
   * A method that is called during instantiation of the controller to set up its data
   */
  private activate() {
    this.getData(1, 15);
  }
}